/**
 * GEDCOM 5.5.1 import — parsing and mapping only.
 *
 * This is the inverse of exportGedcom.js, but it cannot assume it is reading
 * something this app wrote. Files arrive from Ancestry, MyHeritage, Gramps,
 * Family Tree Maker and twenty-year-old desktop programs, and they disagree
 * about nearly everything except the line grammar. So the rule here is: never
 * reject the file over a field, and never invent data to fill a gap. Anything
 * that cannot be represented is dropped and reported as a warning, so the
 * import lands and the person is told exactly what did not survive.
 *
 * Nothing in this file talks to the network. The two-pass write is
 * runGedcomImport.js — see that file for why relationships cannot be sent in
 * the same request that creates the people.
 */

import { LIFE_EVENT_TYPE, PHOTO_NOTE_PREFIX } from './exportGedcom';

// Timeline entries carry an id in the app; the value is opaque, it only has to
// be unique within the person. crypto.randomUUID is not available in every
// environment this runs in (older Safari, the test runner), so there is a
// fallback.
let eventCounter = 0;
function eventId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  eventCounter += 1;
  return `imported-event-${Date.now().toString(36)}-${eventCounter}`;
}

const MONTH_NUMBERS = {
  JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6,
  JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12,
};

// Date approximations and ranges. GEDCOM puts these in front of the date; the
// app has a single date string per event and no way to store "about", so the
// keyword is stripped, the date behind it is kept, and the original text goes
// into notes rather than being silently rounded away.
const DATE_MODIFIERS = new Set([
  'ABT', 'ABOUT', 'CIR', 'CIRCA', 'CAL', 'EST', 'BEF', 'BEFORE', 'AFT',
  'AFTER', 'FROM', 'TO', 'INT', 'MAYBE',
]);

// Standard GEDCOM 5.5.1 individual events. Until 2026-09-18 the importer read
// only BIRT, DEAT, OCCU and EVEN, and discarded every one of these **without a
// warning** — which contradicted this module's own promise that anything
// unrepresentable is reported rather than silently lost. A well researched
// Ancestry export carries more facts in these tags than in the ones that
// survived, and for a migrant family IMMI and NATU are the records that matter
// most.
//
// The app has exactly one place for a dated fact that is not birth or death:
// the timeline. So these land there, through the same path EVEN already used.
const TIMELINE_EVENT_TAGS = {
  ADOP: 'Adoption', BAPM: 'Baptism', BARM: 'Bar Mitzvah', BASM: 'Bat Mitzvah',
  BLES: 'Blessing', BURI: 'Burial', CENS: 'Census', CHR: 'Christening',
  CHRA: 'Adult christening', CONF: 'Confirmation', CREM: 'Cremation',
  EMIG: 'Emigration', FCOM: 'First communion', GRAD: 'Graduation',
  IMMI: 'Immigration', NATU: 'Naturalisation', ORDN: 'Ordination',
  PROB: 'Probate', RESI: 'Residence', RETI: 'Retirement', WILL: 'Will',
};

// Attributes describe a person rather than a moment in their life. Given a
// date they are event enough for the timeline; without one they are facts, and
// a fact with no date has nowhere to sit on a timeline ordered by year, so it
// goes to notes instead of being pinned to a year nobody supplied.
const ATTRIBUTE_TAGS = {
  CAST: 'Caste', DSCR: 'Description', EDUC: 'Education',
  IDNO: 'Identifying number', NATI: 'Nationality', NCHI: 'Children',
  NMR: 'Marriages', PROP: 'Property', RELI: 'Religion',
  SSN: 'Social security number', TITL: 'Title', FACT: 'Fact',
};

// Read elsewhere in this file, or bookkeeping that would be noise on a
// timeline. Listed explicitly so that a tag which is genuinely unknown falls
// through to the warning rather than being quietly swallowed by a catch-all.
const TAGS_HANDLED_OR_IGNORED = new Set([
  'NAME', 'SEX', 'BIRT', 'DEAT', 'OCCU', 'NOTE', 'FAMC', 'FAMS',
  'CHAN', 'RIN', 'RFN', 'AFN', 'SOUR', 'OBJE', 'SUBM', 'ASSO', 'ALIA',
  'ANCI', 'DESI', 'REFN', 'UID',
]);

// "Burial (Brisbane)", "Religion: Methodist", "Census: 1901 (Ipswich)".
function factLabel(base, detail, place) {
  const head = detail ? `${base}: ${detail}` : base;
  return place ? `${head} (${place})` : head;
}

// Field ceilings enforced by the API (people.STRING_LIMITS / LIST_LIMITS). They
// are mirrored rather than imported because a single over-long name would
// otherwise fail validation for the whole 1000-person request. Trimming here
// costs one field; not trimming costs the batch.
export const LIMITS = {
  name: 200,
  occupation: 200,
  birthPlace: 200,
  deathPlace: 200,
  date: 20,
  notes: 10_000,
  parentIds: 16,
  children: 500,
  marriages: 50,
  lifeEvents: 500,
};

// A malformed 10,000-person file can generate a warning per record several
// times over. Past this point they stop being read and start being scrolled,
// so the list is capped and the remainder is counted.
const MAX_WARNINGS = 200;

class WarningLog {
  constructor() {
    this.messages = [];
    this.suppressed = 0;
  }

  add(message) {
    if (this.messages.length >= MAX_WARNINGS) {
      this.suppressed += 1;
      return;
    }
    this.messages.push(message);
  }

  toArray() {
    if (!this.suppressed) return this.messages;
    return [...this.messages, `…and ${this.suppressed} more issues not listed.`];
  }
}

/**
 * Turns the uploaded bytes into text, honouring the file's declared encoding.
 *
 * Worth the trouble because the alternative is silent corruption rather than a
 * visible failure: a Latin-1 file read as UTF-8 does not throw, it just turns
 * every accented surname into a replacement character, and the import looks
 * like it worked. The header that names the encoding is pure ASCII in every
 * encoding involved, so it can be read from a provisional decode.
 *
 * ANSEL is the one that cannot be handled properly. It is the GEDCOM 5.5.1
 * default, no browser ships a decoder for it, and its accented letters are
 * combining characters in the high range — so this decodes as windows-1252,
 * which is right for the ASCII majority of the file, and says so.
 */
export function decodeGedcom(buffer) {
  const bytes = new Uint8Array(buffer);
  const warnings = [];

  // A UTF-8 or UTF-16 BOM is authoritative — it outranks whatever the header
  // claims, because it was written by the program that encoded the bytes.
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: new TextDecoder('utf-16le').decode(bytes), warnings };
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: new TextDecoder('utf-16be').decode(bytes), warnings };
  }
  const hasUtf8Bom = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf;
  const body = hasUtf8Bom ? bytes.subarray(3) : bytes;

  const provisional = new TextDecoder('utf-8').decode(body);
  if (hasUtf8Bom) return { text: provisional, warnings };

  const declared = (provisional.match(/^\s*1\s+CHAR\s+(.+)$/mi)?.[1] || '')
    .trim().toUpperCase();

  if (declared === 'ANSEL') {
    warnings.push(
      'The file declares the ANSEL character set, which browsers cannot decode. '
      + 'Plain letters are correct; accented characters may not be.'
    );
    return { text: new TextDecoder('windows-1252').decode(body), warnings };
  }

  if (declared && !declared.startsWith('UTF')) {
    // ANSI, ASCII, IBMPC, ISO-8859-1 and friends all round-trip through
    // windows-1252 for the byte range they actually use.
    return { text: new TextDecoder('windows-1252').decode(body), warnings };
  }

  // Nothing declared, or UTF-8 declared. Trust UTF-8, but fall back if the
  // bytes are not valid — an undeclared Latin-1 file is common enough.
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(body), warnings };
  } catch (e) {
    warnings.push(
      'The file is not valid UTF-8 and does not say what encoding it uses. '
      + 'It was read as Windows-1252; accented characters may not be right.'
    );
    return { text: new TextDecoder('windows-1252').decode(body), warnings };
  }
}

// `LEVEL [@XREF@] TAG [VALUE]`. Leading whitespace is not legal but several
// exporters emit it, and refusing the file over indentation would be absurd.
const LINE_PATTERN = /^\s*(\d+)\s+(?:@([^@]*)@\s+)?([A-Za-z0-9_.]+)(?:\s(.*))?$/;

/**
 * Parses the line grammar into a node tree: `{ tag, xref, value, children }`.
 *
 * CONC and CONT are folded into the parent's value here rather than left for
 * callers, since they are a transport detail — a long note is split across
 * lines because GEDCOM caps line length, not because it has structure.
 */
export function parseGedcomRecords(text, warnings = new WarningLog()) {
  const records = [];
  // stack[n] is the node currently open at level n.
  const stack = [];
  let lineNumber = 0;

  for (const rawLine of text.split(/\r\n|\r|\n/)) {
    lineNumber += 1;
    const line = rawLine.replace(/^﻿/, '');
    if (!line.trim()) continue;

    const match = line.match(LINE_PATTERN);
    if (!match) {
      warnings.add(`Line ${lineNumber} is not valid GEDCOM and was skipped.`);
      continue;
    }

    const level = parseInt(match[1], 10);
    const xref = match[2] || null;
    const tag = match[3].toUpperCase();
    const value = match[4] === undefined ? '' : match[4];

    if (tag === 'CONC' || tag === 'CONT') {
      const parent = stack[level - 1];
      if (!parent) {
        warnings.add(`Line ${lineNumber} continues a line that isn't there; skipped.`);
        continue;
      }
      parent.value += (tag === 'CONT' ? '\n' : '') + value;
      continue;
    }

    const node = { tag, xref, value, children: [] };

    if (level === 0) {
      records.push(node);
      stack.length = 0;
      stack[0] = node;
      continue;
    }

    const parent = stack[level - 1];
    if (!parent) {
      // A level that skips a step — 0 then 2. The record it belongs to is
      // ambiguous, so it is dropped rather than guessed at.
      warnings.add(`Line ${lineNumber} is nested under nothing and was skipped.`);
      continue;
    }
    parent.children.push(node);
    stack.length = level;
    stack[level] = node;
  }

  return records;
}

const child = (node, tag) => node?.children.find(c => c.tag === tag);
const childValue = (node, tag) => child(node, tag)?.value?.trim() || null;

// `@I1@` → `I1`. Values that are not pointers come back null so a stray
// free-text HUSB can't be mistaken for a reference.
function pointer(value) {
  const match = (value || '').trim().match(/^@([^@]+)@$/);
  return match ? match[1] : null;
}

/**
 * A GEDCOM date as the closest ISO date the app can store.
 *
 * Returns `{ iso, approximate }`. Partial dates are the norm in real files —
 * "JAN 1922" and "1922" are complete answers, not truncated ones — so they come
 * back as `1922-01` and `1922` rather than being padded out to a day nobody
 * recorded. `approximate` marks a date that carried a qualifier the app has
 * nowhere to store; callers keep the original text in notes when it is set.
 */
export function gedcomDateToIso(raw) {
  if (!raw) return { iso: null, approximate: false };

  let text = String(raw).trim().toUpperCase();
  if (!text || text === 'Y') return { iso: null, approximate: false };

  // A calendar escape (@#DHEBREW@, @#DFRENCH R@) means the numbers that follow
  // are not Gregorian. Converting them is a genuine calendar conversion, not a
  // reformat, so the date is dropped and reported.
  if (text.startsWith('@#')) return { iso: null, approximate: false, unsupported: true };

  let approximate = false;

  // "INT 12 JAN 1900 (as recorded in the family bible)"
  const parenthetical = text.indexOf('(');
  if (parenthetical >= 0) {
    text = text.slice(0, parenthetical).trim();
    approximate = true;
  }

  // Ranges and periods: keep the earlier bound, which is the one that sorts and
  // displays sensibly.
  const range = text.match(/^(?:BET|BETWEEN|FROM)\s+(.+?)\s+(?:AND|TO)\s+.+$/);
  if (range) {
    text = range[1].trim();
    approximate = true;
  }

  const words = text.split(/\s+/);
  while (words.length && DATE_MODIFIERS.has(words[0])) {
    words.shift();
    approximate = true;
  }
  text = words.join(' ');

  // Dual dating: "12 FEB 1745/46" is Julian/Gregorian for the same year. The
  // first is what the record says.
  text = text.replace(/^(.*?)(\d{3,4})\/\d{1,4}$/, '$1$2');

  if (!text) return { iso: null, approximate };

  const pad = (n, width) => String(n).padStart(width, '0');
  const monthNumber = (name) => MONTH_NUMBERS[name.slice(0, 3)];

  let match = text.match(/^(\d{1,2})\s+([A-Z]{3,})\s+(\d{1,4})$/);
  if (match) {
    const month = monthNumber(match[2]);
    if (month) {
      return {
        iso: `${pad(match[3], 4)}-${pad(month, 2)}-${pad(match[1], 2)}`,
        approximate,
      };
    }
  }

  match = text.match(/^([A-Z]{3,})\s+(\d{1,4})$/);
  if (match) {
    const month = monthNumber(match[1]);
    if (month) return { iso: `${pad(match[2], 4)}-${pad(month, 2)}`, approximate };
  }

  match = text.match(/^(\d{1,4})$/);
  if (match) return { iso: pad(match[1], 4), approximate };

  return { iso: null, approximate, unsupported: true };
}

/**
 * The name as one string.
 *
 * GEDCOM marks the surname with slashes — `John /Smith/`, and `/Smith/ Jr` for
 * a suffix — so removing the slashes and collapsing the gap is the whole job
 * for the common case. GIVN/SURN subtags are the fallback for the files that
 * leave the NAME value empty.
 */
function personName(indi) {
  const nameNode = child(indi, 'NAME');
  let name = (nameNode?.value || '').replace(/\//g, ' ').replace(/\s+/g, ' ').trim();

  if (!name && nameNode) {
    name = [childValue(nameNode, 'GIVN'), childValue(nameNode, 'SURN')]
      .filter(Boolean).join(' ').trim();
  }
  return name;
}

// BIRT/DEAT carry the date and place as subtags, and either can be absent.
function eventDetail(indi, tag, label, notes, warnings, xref) {
  const node = child(indi, tag);
  if (!node) return { date: null, place: null };

  const rawDate = childValue(node, 'DATE');
  const { iso, approximate, unsupported } = gedcomDateToIso(rawDate);

  if (rawDate && !iso) {
    // The date is unrepresentable, not absent. Keeping the source text in notes
    // is the difference between "we couldn't store this" and losing it.
    notes.push(`${label} date recorded as: ${rawDate}`);
    if (unsupported) {
      warnings.add(`${xref}: ${label.toLowerCase()} date "${rawDate}" isn't a date this app can store; kept in notes.`);
    }
  } else if (rawDate && approximate) {
    notes.push(`${label} date recorded as: ${rawDate}`);
  }

  return {
    date: iso ? iso.slice(0, LIMITS.date) : null,
    place: childValue(node, 'PLAC'),
  };
}

/**
 * How a marriage ended, if the FAM says.
 *
 * DIV and ANUL are the only endings GEDCOM has tags for. A death is normally
 * left implicit — the spouse's DEAT record says it — and a legal separation has
 * no representation at all, so this app writes both as a custom `EVEN` with a
 * `TYPE` naming the reason, and reads them back here. The TYPE text comes from
 * MARRIAGE_END_TYPES in exportGedcom.js; the matching is loose because other
 * products word their custom events differently.
 *
 * Order matters: "Marriage ended by death of spouse" contains both "death" and
 * "ended", and the specific reading is the right one.
 */
function marriageEnd(fam) {
  const divorce = child(fam, 'DIV');
  if (divorce) return { node: divorce, reason: 'divorce' };

  const annulment = child(fam, 'ANUL');
  if (annulment) return { node: annulment, reason: 'annulment' };

  for (const event of fam.children.filter(c => c.tag === 'EVEN')) {
    const type = (childValue(event, 'TYPE') || '').toLowerCase();
    if (type.includes('separat')) return { node: event, reason: 'separation' };
    if (type.includes('death') || type.includes('widow')) return { node: event, reason: 'death' };
    if (type.includes('end')) return { node: event, reason: null };
  }
  return null;
}

// NOTE is either inline text or a pointer to a top-level NOTE record.
function noteText(node, noteRecords) {
  const target = pointer(node.value);
  if (target) return noteRecords.get(target) || null;
  return node.value.trim() || null;
}

function truncate(value, limit) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return trimmed.length > limit ? trimmed.slice(0, limit) : trimmed;
}

/**
 * Reads a GEDCOM file into people the API can be handed.
 *
 * Returns `{ people, warnings, stats }`, where each person is
 * `{ xref, data, rel }`: `data` is only the person's own attributes, because
 * that is all pass 1 of the import can send. `rel` holds relationships still
 * expressed in GEDCOM xrefs — pass 2 rewrites them once the server has minted
 * real ids.
 */
export function importGedcom(text) {
  const warnings = new WarningLog();
  const records = parseGedcomRecords(text, warnings);

  const noteRecords = new Map();
  const indiRecords = [];
  const famRecords = [];

  for (const record of records) {
    if (record.tag === 'NOTE' && record.xref) noteRecords.set(record.xref, record.value.trim());
    else if (record.tag === 'INDI' && record.xref) indiRecords.push(record);
    else if (record.tag === 'FAM' && record.xref) famRecords.push(record);
  }

  if (!indiRecords.length) {
    return {
      people: [],
      warnings: warnings.toArray(),
      stats: { people: 0, families: 0, unnamed: 0 },
    };
  }

  // ── People ────────────────────────────────────────────────────────────────
  const byXref = new Map();
  let unnamed = 0;
  // Tag -> how many times it was seen and could not be mapped. Aggregated
  // across the whole file and reported once: a 600 person import that warned
  // per occurrence would produce thousands of lines, which is the same as
  // warning about nothing.
  const unmapped = new Map();

  for (const indi of indiRecords) {
    if (byXref.has(indi.xref)) {
      warnings.add(`Two records share the id @${indi.xref}@; the second was skipped.`);
      continue;
    }

    const notes = [];
    let name = truncate(personName(indi), LIMITS.name);
    if (!name) {
      // `name` is required by the API and a person with none is still a real
      // node in the tree — usually a spouse or parent known only as a link.
      name = 'Unknown';
      unnamed += 1;
    }

    const birth = eventDetail(indi, 'BIRT', 'Birth', notes, warnings, `@${indi.xref}@`);
    const death = eventDetail(indi, 'DEAT', 'Death', notes, warnings, `@${indi.xref}@`);

    const sex = (childValue(indi, 'SEX') || '').toUpperCase().charAt(0);

    for (const noteNode of indi.children.filter(c => c.tag === 'NOTE')) {
      const note = noteText(noteNode, noteRecords);
      if (!note) continue;
      // Skip this app's own photo marker. The photo itself cannot be restored —
      // the URL points into the Storage of whichever tree it was exported from,
      // with no `photoStoragePath` to make deletion or quota accounting work —
      // and keeping the line as a note meant every export/import cycle stacked
      // another copy of the URL into the person's notes.
      if (note.startsWith(PHOTO_NOTE_PREFIX)) continue;
      notes.push(note);
    }

    // Timeline entries: this app's own `EVEN` records, and any other custom
    // event a different product wrote. The app has somewhere to put these and
    // nothing else does the job, so a foreign event is kept too, with its own
    // TYPE as the label.
    const lifeEvents = [];
    const addEvent = (label, year, color = null) => {
      if (!label && !year) return;
      if (lifeEvents.length >= LIMITS.lifeEvents) return;
      lifeEvents.push({
        id: eventId(),
        year,
        text: truncate(label, LIMITS.name) || '',
        color,
      });
    };

    for (const node of indi.children) {
      const tag = node.tag;
      // Product specific extensions. Guessing at another vendor's private tag
      // is how you invent data, so they are skipped without a warning: they are
      // not standard facts we failed to handle.
      if (tag.startsWith('_')) continue;
      if (TAGS_HANDLED_OR_IGNORED.has(tag)) continue;

      const year = (gedcomDateToIso(childValue(node, 'DATE')).iso || '').slice(0, 4);
      const place = (childValue(node, 'PLAC') || '').trim();

      if (tag === 'EVEN') {
        const type = (childValue(node, 'TYPE') || '').trim();
        const text = (childValue(node, 'NOTE') || node.value || '').trim();
        const base = type && type !== LIFE_EVENT_TYPE
          ? [type, text].filter(Boolean).join(': ')
          : text;
        const label = place ? `${base} (${place})`.trim() : base;
        if (year) addEvent(label, year, childValue(node, '_COLOR') || null);
        else if (label) notes.push(label);
        continue;
      }

      const base = TIMELINE_EVENT_TAGS[tag] || ATTRIBUTE_TAGS[tag];
      if (base) {
        // `1 CENS Y` and `1 RESI Y` mean only "this happened"; the Y is not a
        // detail and printing it would read as a value.
        const raw = (node.value || '').trim();
        const detail = raw === 'Y' ? '' : raw;
        const label = factLabel(base, detail, place);
        if (year) addEvent(label, year);
        else notes.push(label);
        continue;
      }

      unmapped.set(tag, (unmapped.get(tag) || 0) + 1);
    }

    const data = {
      name,
      birth: birth.date,
      death: death.date,
      birthPlace: truncate(birth.place, LIMITS.birthPlace),
      deathPlace: truncate(death.place, LIMITS.deathPlace),
      occupation: truncate(childValue(indi, 'OCCU'), LIMITS.occupation),
      // Anything other than M or F is left unset rather than defaulted. The
      // canvas treats a missing gender as it treats "not female", and guessing
      // would put a fabricated fact on the record.
      gender: sex === 'M' || sex === 'F' ? sex : null,
      notes: truncate(notes.join('\n\n'), LIMITS.notes),
      lifeEvents,
    };

    byXref.set(indi.xref, {
      xref: indi.xref,
      data,
      rel: { parents: [], children: [], marriages: [] },
    });
  }

  // ── Families ──────────────────────────────────────────────────────────────
  // The app has no family entity: a marriage is a record mirrored on both
  // spouses, and children hang off both the marriage and each parent. So every
  // FAM is expanded into edges on the people it names.
  const known = (xref) => (xref && byXref.has(xref) ? xref : null);
  const seenCouples = new Map(); // "a|b" → the marriage entry already created

  let families = 0;

  for (const fam of famRecords) {
    const husband = known(pointer(childValue(fam, 'HUSB')));
    const wife = known(pointer(childValue(fam, 'WIFE')));

    const childXrefs = [];
    for (const chil of fam.children.filter(c => c.tag === 'CHIL')) {
      const target = known(pointer(chil.value));
      if (!target) {
        warnings.add(`Family @${fam.xref}@ lists a child who has no record in the file.`);
        continue;
      }
      if (!childXrefs.includes(target)) childXrefs.push(target);
    }

    if (!husband && !wife && !childXrefs.length) continue;
    families += 1;

    const parents = [husband, wife].filter(Boolean);

    for (const childXref of childXrefs) {
      const person = byXref.get(childXref);
      for (const parentXref of parents) {
        if (!person.rel.parents.includes(parentXref)) person.rel.parents.push(parentXref);
        const parent = byXref.get(parentXref);
        if (!parent.rel.children.includes(childXref)) parent.rel.children.push(childXref);
      }
    }

    if (!husband || !wife) continue; // Single-parent family: no marriage to record.

    const marriage = child(fam, 'MARR');
    const ending = marriageEnd(fam);

    const coupleKey = husband < wife ? `${husband}|${wife}` : `${wife}|${husband}`;
    const existing = seenCouples.get(coupleKey);
    if (existing) {
      // The same pair in two FAM records. The app keys a marriage by spouse, so
      // a second entry for the same person would render as two marriages to
      // one spouse; the children are merged into the entry that exists.
      for (const childXref of childXrefs) {
        if (!existing.children.includes(childXref)) existing.children.push(childXref);
      }
      warnings.add(
        `Families @${fam.xref}@ and @${existing.famXref}@ record the same couple; they were merged.`
      );
      continue;
    }

    const marriageDate = gedcomDateToIso(childValue(marriage, 'DATE')).iso;
    const endDate = gedcomDateToIso(childValue(ending?.node, 'DATE')).iso;

    const entry = {
      famXref: fam.xref,
      husband,
      wife,
      marriageDate: marriageDate ? marriageDate.slice(0, LIMITS.date) : null,
      marriagePlace: truncate(childValue(marriage, 'PLAC'), LIMITS.birthPlace),
      status: ending ? 'ended' : 'current',
      endReason: ending ? ending.reason : null,
      endDate: endDate ? endDate.slice(0, LIMITS.date) : null,
      children: [...childXrefs],
    };
    seenCouples.set(coupleKey, entry);

    // Mirrored onto both spouses, which is how the app stores marriages.
    for (const [self, spouse] of [[husband, wife], [wife, husband]]) {
      byXref.get(self).rel.marriages.push({
        spouseXref: spouse,
        marriageDate: entry.marriageDate,
        marriagePlace: entry.marriagePlace,
        status: entry.status,
        endReason: entry.endReason,
        endDate: entry.endDate,
        children: entry.children, // shared array: a merge above updates both
      });
    }
  }

  // ── Ceilings ──────────────────────────────────────────────────────────────
  // Enforced after the expansion rather than during it, since only now is the
  // final count known. Over-limit lists are trimmed so one unusual record
  // cannot fail the request that carries a thousand ordinary ones.
  for (const person of byXref.values()) {
    for (const [field, limit] of [
      ['parents', LIMITS.parentIds],
      ['children', LIMITS.children],
      ['marriages', LIMITS.marriages],
    ]) {
      const list = person.rel[field];
      if (list.length > limit) {
        warnings.add(
          `@${person.xref}@ has ${list.length} ${field}, more than the ${limit} this app stores; the extras were dropped.`
        );
        list.length = limit;
      }
    }
  }

  // One line for the whole file, naming the tags and how many of each. The
  // point is that somebody who imports a heavily researched tree is told that
  // something did not come across, rather than seeing a clean success and
  // discovering the gap months later.
  if (unmapped.size) {
    const total = [...unmapped.values()].reduce((sum, n) => sum + n, 0);
    const listed = [...unmapped.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([tag, n]) => `${tag} (${n})`);
    warnings.add(
      `${total} fact${total === 1 ? '' : 's'} could not be imported and ${total === 1 ? 'was' : 'were'} left out: `
      + listed.join(', ')
      + (unmapped.size > 6 ? ', and others.' : '.')
    );
  }

  return {
    people: [...byXref.values()],
    warnings: warnings.toArray(),
    stats: { people: byXref.size, families, unnamed },
  };
}
